import React, { useState } from 'react';
import {
  Users,
  UserCheck,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  Tag,
  Filter,
  Sparkles,
  Lock,
  EyeOff,
  BellOff,
  Layers
} from 'lucide-react';
import { INITIAL_CRM_USER_SAMPLE } from '../../data/growthData';
import { UserLifecycleStage, CrmUserProfile } from '../../types';

export const UserLifecycleCrmView: React.FC = () => {
  const [profile, setProfile] = useState<CrmUserProfile>(INITIAL_CRM_USER_SAMPLE);
  const [selectedStage, setSelectedStage] = useState<UserLifecycleStage>('ALL' as UserLifecycleStage);
  const [activeTab, setActiveTab] = useState<'lifecycle_stages' | 'sample_profile' | 'privacy_rules'>('lifecycle_stages');

  const STAGES_LIST: { stage: UserLifecycleStage; name: string; description: string; count: number; badgeColor: string }[] = [
    { stage: 'VISITOR', name: '1. Visitor', description: 'Anonymous user on site. No tool calculations completed yet.', count: 312000, badgeColor: 'bg-slate-100 text-slate-700' },
    { stage: 'ENGAGED_VISITOR', name: '2. Engaged Visitor', description: 'Opened a financial tool or guide.', count: 184000, badgeColor: 'bg-blue-50 text-blue-700 border-blue-200' },
    { stage: 'ACTIVATED_VISITOR', name: '3. Activated Visitor', description: 'Executed at least 1 successful useful financial calculation.', count: 88000, badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { stage: 'REGISTERED', name: '4. Registered Account', description: 'Created account via Passkey / Magic Link without password friction.', count: 34200, badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { stage: 'ACCOUNT_ACTIVATED', name: '5. Account Activated', description: 'Saved first calculation or created exchange rate alert.', count: 21500, badgeColor: 'bg-purple-50 text-purple-700 border-purple-200' },
    { stage: 'RETAINED', name: '6. Retained User', description: 'Returned for recurring useful calculations within 30 days.', count: 18900, badgeColor: 'bg-teal-50 text-teal-700 border-teal-200' },
    { stage: 'HIGH_INTENT', name: '7. High Intent', description: 'Uses multiple tools (Salary + Loan + Transfers) frequently.', count: 6400, badgeColor: 'bg-amber-50 text-amber-800 border-amber-200' },
    { stage: 'DORMANT', name: '8. Dormant', description: 'No meaningful activity in configured window (30-90 days).', count: 12400, badgeColor: 'bg-rose-50 text-rose-700 border-rose-200' },
    { stage: 'REACTIVATED', name: '9. Reactivated', description: 'Returned after dormancy following a relevant product/tax update.', count: 4120, badgeColor: 'bg-sky-50 text-sky-700 border-sky-200' },
    { stage: 'ADVOCATE', name: '10. Advocate', description: 'Has referred qualified peers to YamooPay.', count: 1850, badgeColor: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  ];

  const filteredStages = selectedStage === ('ALL' as UserLifecycleStage)
    ? STAGES_LIST
    : STAGES_LIST.filter(s => s.stage === selectedStage);

  const toggleMarketingConsent = () => {
    setProfile(prev => ({
      ...prev,
      consentStatus: {
        ...prev.consentStatus,
        marketingConsent: !prev.consentStatus.marketingConsent
      }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-900">
              P12.1 — CRM Core Engine
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <Lock className="w-3 h-3 text-emerald-600" />
              Non-Financial CRM Profile Guaranteed
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            User Lifecycle State & Non-Sensitive CRM Profile Engine
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Orchestrates 10 deterministic user lifecycle stages. Strictly separates Product Retention State from Marketing Permission — ensuring users receive high-value product alerts without unwanted promotional spam.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('lifecycle_stages')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'lifecycle_stages' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Lifecycle Taxonomy
          </button>
          <button
            onClick={() => setActiveTab('sample_profile')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'sample_profile' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            CRM Profile Inspector
          </button>
          <button
            onClick={() => setActiveTab('privacy_rules')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'privacy_rules' ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Privacy Firewall Rules
          </button>
        </div>
      </div>

      {activeTab === 'lifecycle_stages' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-bold text-slate-700">Filter Lifecycle Stage:</span>
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value as UserLifecycleStage)}
                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white font-medium text-slate-800"
              >
                <option value="ALL">All 10 Stages</option>
                {STAGES_LIST.map(s => (
                  <option key={s.stage} value={s.stage}>{s.name}</option>
                ))}
              </select>
            </div>
            <span className="text-xs font-medium text-slate-500">
              Total Managed CRM Users: <strong className="text-slate-900">519,270</strong>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStages.map((item) => (
              <div key={item.stage} className="bg-white p-4 rounded-xl border border-slate-200 hover:border-blue-300 transition-all shadow-xs">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${item.badgeColor}`}>
                    {item.name}
                  </span>
                  <span className="text-xs font-bold text-slate-900 bg-slate-100 px-2.5 py-0.5 rounded-full">
                    {item.count.toLocaleString()} users
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">
                  {item.description}
                </p>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    Event-driven transition
                  </span>
                  <span className="font-mono text-slate-400">
                    STAGE_KEY: {item.stage}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'sample_profile' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="md:col-col-span-1 bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-900 font-bold text-sm">
                YP
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Sample CRM Profile</h3>
                <p className="text-xs font-mono text-slate-500">{profile.userId}</p>
              </div>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Lifecycle Stage:</span>
                <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                  {profile.lifecycleStage}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Locale & Timezone:</span>
                <span className="font-semibold text-slate-800">{profile.locale} ({profile.timezone})</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Market Preference:</span>
                <span className="font-semibold text-slate-800">{profile.marketPreference}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Retained Status:</span>
                <span className="font-bold text-emerald-700">ACTIVE (D0)</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Dormant Days:</span>
                <span className="font-semibold text-slate-800">{profile.dormantDays} days</span>
              </div>
            </div>

            <div className="pt-2">
              <span className="text-xs font-bold text-slate-700 block mb-2">Non-Sensitive Product Usage Tags:</span>
              <div className="flex flex-wrap gap-1.5">
                {profile.productUsageTags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-mono border border-slate-200 flex items-center gap-1">
                    <Tag className="w-2.5 h-2.5 text-slate-400" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Consent & Preference Controls */}
          <div className="md:col-span-2 bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-5">
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-blue-900" />
                Communication Preferences & Consent Separation
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Demonstrates YamooPay Rule #8: Product State is strictly isolated from Marketing Consent.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Essential Security Email</span>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">ALWAYS MANDATORY</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Passkey updates, security alerts, and login notifications.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Requested Financial Alerts</span>
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">OPT-IN ACTIVATED</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Explicitly requested FX rate alerts and corridor quote changes.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800">Product Updates (Tax Years)</span>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">ACTIVE</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Notifications when new official tax tables or formulas are published.
                </p>
              </div>

              <div className={`p-3 rounded-lg border transition-all space-y-2 ${
                profile.consentStatus.marketingConsent ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900">Promotional Marketing Consent</span>
                  <button
                    onClick={toggleMarketingConsent}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer ${
                      profile.consentStatus.marketingConsent ? 'bg-emerald-700 text-white' : 'bg-rose-700 text-white'
                    }`}
                  >
                    {profile.consentStatus.marketingConsent ? 'GRANTED' : 'OPTED OUT'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-600">
                  {profile.consentStatus.marketingConsent
                    ? 'User has explicitly granted marketing email permission.'
                    : 'User is opted out. Zero promotional campaigns will be sent.'}
                </p>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-900 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-bold text-blue-950">Global Frequency Cap & Quiet Hours Protection</h4>
                <p className="text-xs text-blue-900 mt-0.5 leading-relaxed">
                  Frequency cap enforced: Maximum 1 lifecycle message per week. Quiet Hours enabled (22:00 - 08:00 local time). Even if a marketing campaign triggers, it will be automatically queued until quiet hours expire.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'privacy_rules' && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-rose-700" />
            <h3 className="text-base font-bold text-slate-900">
              YamooPay Strict Financial Privacy & CRM Anti-Profiling Policy
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-rose-50 rounded-xl border border-rose-200 space-y-2">
              <div className="flex items-center gap-2 text-rose-900 font-bold text-xs">
                <XCircle className="w-4 h-4 text-rose-700" />
                STRICTLY PROHIBITED CRM DATA ATTRIBUTES
              </div>
              <ul className="text-xs text-rose-800 space-y-1.5 pl-5 list-disc">
                <li>Exact salary amount (e.g. "Earns $87,432")</li>
                <li>Exact debt or mortgage balance (e.g. "Owes $412,881")</li>
                <li>Financial distress profiling (e.g. "High debt user")</li>
                <li>Exact income tax calculated</li>
                <li>Private bank account or card information</li>
              </ul>
            </div>

            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-2">
              <div className="flex items-center gap-2 text-emerald-900 font-bold text-xs">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                ALLOWED CRM PRODUCT USAGE ATTRIBUTES
              </div>
              <ul className="text-xs text-emerald-800 space-y-1.5 pl-5 list-disc">
                <li>Has used Salary Calculator (boolean tag)</li>
                <li>Preferred market / locale (e.g. "US / en-US")</li>
                <li>Has active Exchange Rate Alert (boolean tag)</li>
                <li>Saved Calculation Count (numeric count)</li>
                <li>Lifecycle Stage (e.g. "ACTIVATED_VISITOR")</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
